import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useZustandAuth } from '../../hooks/useZustandAuth';
import { useAuthStore } from '../../stores/authStore';
import { RoleLevel, ROLE_LABELS } from '../../types/rbacImproved';
import { SuperAdminOnly, AdminOnly } from '../../components/PermissionGuards';
import UserProfileModal from '../../components/UserProfileModal';
import EditPermissionsModal from '../../components/EditPermissionsModal';
import PreRegistrationModal from '../../components/PreRegistrationModal';
import { apiClient } from '../../utils/apiClient';
import { activityTracker } from '../../services/activityTracker';
import { getAssignableRoleOptions } from '../../utils/roleConfig';
import { notify } from '../../utils/notifications';
import { PageWrapper, HeroSection, HeroTitle, HeroSubtitle, HeroActions, StatsGrid, StatCard, MainContent, SearchFilterBar, SearchInputWrapper, FilterSelectWrapper, InputLabel, SearchInput, FilterSelect, SectionTitle, UsersGrid, UserCard, UserCardHeader, UserInfo, UserName, UserEmail, UserBusiness, UserBadges, StatusBadge, RoleBadge, UserCardBody, UserDetails, DetailItem, UserCardActions, RoleSelector, PrimaryButton, SecondaryButton, ActionButton, ErrorBanner, EmptyState, AdminSection, AdminSectionTitle, AdminActionsGrid, LoadingWrapper, LoadingSpinner, LoadingText, LoadingSubText } from './UserManagement.styles';
const UserManagement = () => {
    const navigate = useNavigate();
    const { user: currentUser, getMaxAssignableRole, canManageUser } = useZustandAuth();
    const authStore = useAuthStore();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [error, setError] = useState('');
    // Modal states
    const [selectedUser, setSelectedUser] = useState(null);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
    const [isPreRegistrationModalOpen, setIsPreRegistrationModalOpen] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    // Request deduplication
    const isFetchingRef = useRef(false);
    // Fetch users function - now using centralized API client with deduplication
    const fetchUsers = async () => {
        // Prevent duplicate concurrent requests
        if (isFetchingRef.current) {
            console.log('⏭️ UserManagement - Fetch already in progress, skipping');
            return;
        }
        isFetchingRef.current = true;
        try {
            setIsLoading(true);
            setError('');
            console.log('🔍 UserManagement - Fetching users...');
            const response = await apiClient.get('users-get-all');
            if (response.ok && response.data) {
                if (response.data.success && response.data.users) {
                    console.log('✅ UserManagement - Users loaded:', response.data.users.length);
                    setUsers(response.data.users);
                    setFilteredUsers(response.data.users);
                }
                else {
                    console.error('❌ UserManagement - API returned unsuccessful response:', response.data);
                    setError(response.data.error || 'Failed to load users');
                    setUsers([]);
                    setFilteredUsers([]);
                }
            }
            else {
                console.error('❌ UserManagement - Request failed:', response.error);
                setError(response.error || `Failed to fetch users: ${response.status} ${response.statusText}`);
                setUsers([]);
                setFilteredUsers([]);
            }
        }
        catch (error) {
            console.error('❌ UserManagement - Unexpected error:', error);
            setError('Unexpected error: Failed to load users');
            setUsers([]);
            setFilteredUsers([]);
        }
        finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    };
    // Initial fetch - wait for auth to be ready
    useEffect(() => {
        // Only fetch when auth is fully initialized
        if (authStore.isAuthenticated && !authStore.isLoading) {
            fetchUsers();
        }
    }, [authStore.isAuthenticated, authStore.isLoading]);
    // Make debug helper available globally for troubleshooting (development only)
    useEffect(() => {
        // Only expose debug helper in development
        if (process.env.NODE_ENV !== 'development')
            return;
        if (typeof window !== 'undefined') {
            window.debugUserManagement = () => {
                console.log('🔧 User Management Debug Info:');
                console.log('Current state:', {
                    usersCount: users.length,
                    filteredUsersCount: filteredUsers.length,
                    isLoading,
                    error,
                    searchTerm,
                    roleFilter
                });
                console.log('Auth token:', {
                    exists: !!authStore.accessToken,
                    length: authStore.accessToken?.length || 0
                });
                // Try to call the API manually
                console.log('Testing API call...');
                const testUrl = 'http://localhost:7071/api/users-get-all';
                fetch(testUrl, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${authStore.accessToken}`,
                        'Content-Type': 'application/json'
                    }
                }).then(async (response) => {
                    console.log('Manual API test result:', {
                        status: response.status,
                        statusText: response.statusText,
                        ok: response.ok,
                        contentType: response.headers.get('content-type'),
                        url: response.url
                    });
                    const text = await response.text();
                    console.log('Response body preview:', text.substring(0, 300));
                    console.log('Is HTML?', text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html'));
                }).catch(err => {
                    console.error('Manual API test failed:', err);
                });
            };
            console.log('💡 Debug helper available: window.debugUserManagement()');
        }
        return () => {
            if (typeof window !== 'undefined') {
                delete window.debugUserManagement;
            }
        };
    }, []); // Empty dependency array to prevent infinite loop
    // Filter users based on search and role
    useEffect(() => {
        let filtered = users;
        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(user => user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.profile.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.profile.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.profile.businessName?.toLowerCase().includes(searchTerm.toLowerCase()));
        }
        // Role filter
        if (roleFilter !== 'all') {
            filtered = filtered.filter(user => user.role === roleFilter);
        }
        setFilteredUsers(filtered);
    }, [users, searchTerm, roleFilter]);
    const handleRoleChange = async (userId, newRole) => {
        try {
            console.log('🔄 Updating user role:', { userId, newRole });
            const response = await apiClient.put(`user-role-update/${userId}`, { role: newRole });
            console.log('🔄 Role update response:', {
                ok: response.ok,
                status: response.status,
                data: response.data,
                error: response.error
            });
            if (response.ok && response.data?.success) {
                activityTracker.trackAdminAction('updated-user-role', `Changed role for user ${userId} to ${newRole}`, { targetUserId: userId, newRole });
                await fetchUsers();
                notify.success('Role Updated', 'User role has been updated successfully');
            }
            else {
                notify.error('Update Failed', response.error || response.data?.error || 'Failed to update role');
            }
        }
        catch (error) {
            console.error('❌ Role update failed:', error);
            notify.error('Update Failed', 'Failed to update role');
        }
    };
    const handleStatusChange = async (userId, newStatus) => {
        try {
            const response = await apiClient.put(`user-status-update/${userId}`, { status: newStatus });
            if (response.ok && response.data?.success) {
                activityTracker.trackAdminAction('updated-user-status', `Changed status for user ${userId} to ${newStatus}`, { targetUserId: userId, newStatus });
                await fetchUsers();
                notify.success('Status Updated', 'User status has been updated successfully');
            }
            else {
                notify.error('Update Failed', response.error || 'Failed to update status');
            }
        }
        catch (error) {
            console.error('Status update failed:', error);
            notify.error('Update Failed', 'Failed to update status');
        }
    };
    const handleSellingPermissionToggle = async (userId, currentPermission) => {
        try {
            const newPermission = currentPermission === 'permitted' ? 'denied' : 'permitted';
            const response = await apiClient.patch(`admin/users/${userId}/selling-permission`, {
                sellingPermission: newPermission
            });
            if (response.ok && response.data?.success) {
                activityTracker.trackAdminAction('updated-selling-permission', `Selling permission ${newPermission} for user ${userId}`, { targetUserId: userId, newPermission });
                await fetchUsers();
                notify.success('Selling Permission Updated', `Selling permission has been ${newPermission === 'permitted' ? 'granted' : 'denied'}`);
            }
            else {
                notify.error('Update Failed', response.error || response.data?.error || 'Failed to update selling permission');
            }
        }
        catch (error) {
            console.error('Selling permission update failed:', error);
            notify.error('Update Failed', 'Failed to update selling permission');
        }
    };
    const getRoleColor = (roleLevel) => {
        if (roleLevel >= RoleLevel.PLATFORM_ADMIN)
            return '#FF6B6B'; // Red for admins
        if (roleLevel >= RoleLevel.SALES_MANAGER)
            return '#4ECDC4'; // Teal for managers
        if (roleLevel >= RoleLevel.SENIOR_DEALER)
            return '#45B7D1'; // Blue for senior
        if (roleLevel >= RoleLevel.VERIFIED_DEALER)
            return '#96CEB4'; // Green for verified
        return '#FFEAA7'; // Yellow for basic/applicant
    };
    // Modal handlers
    const handleViewProfile = async (user) => {
        setSelectedUser(user);
        setIsProfileModalOpen(true);
        // Fetch detailed user data
        try {
            const response = await apiClient.get(`user-profile/${user.id}`);
            if (response.ok && response.data?.user) {
                setUserDetails(response.data.user);
            }
        }
        catch (error) {
            console.error('Failed to fetch user details:', error);
        }
    };
    const handleEditPermissions = (user) => {
        setSelectedUser(user);
        setIsPermissionsModalOpen(true);
    };
    const handleSavePermissions = async (userId, permissions) => {
        try {
            const response = await apiClient.put(`user-permissions-update/${userId}`, permissions);
            if (response.ok) {
                await fetchUsers();
                notify.success('Permissions Updated', 'User permissions have been updated successfully');
            }
            else {
                notify.error('Update Failed', response.error || 'Failed to update permissions');
            }
        }
        catch (error) {
            console.error('Permissions update failed:', error);
            notify.error('Update Failed', 'Failed to update permissions');
        }
    };
    const maxAssignableRole = getMaxAssignableRole();
    const userStats = {
        total: users.length,
        active: users.filter(u => u.status === 'active').length,
        pending: users.filter(u => u.status === 'pending').length,
        dealers: users.filter(u => u.roleLevel >= RoleLevel.BASIC_DEALER && u.roleLevel <= RoleLevel.SENIOR_DEALER).length
    };
    if (isLoading) {
        return (_jsx(PageWrapper, { children: _jsxs(LoadingWrapper, { children: [_jsx(LoadingSpinner, {}), _jsx(LoadingText, { children: "Loading User Management" }), _jsx(LoadingSubText, { children: "Fetching users from database..." })] }) }));
    }
    return (_jsxs(PageWrapper, { children: [_jsxs(HeroSection, { children: [_jsxs(HeroTitle, { id: "tour-user-management-title", children: ["User ", _jsx("span", { children: "Management" })] }), _jsx(HeroSubtitle, { children: "Manage dealer accounts, roles, and permissions across the platform" }), _jsxs(HeroActions, { children: [_jsx(PrimaryButton, { id: "tour-create-registration-button", onClick: () => setIsPreRegistrationModalOpen(true), children: "+ Create Registration Link" }), _jsx(SecondaryButton, { onClick: fetchUsers, disabled: isLoading, children: isLoading ? 'Refreshing...' : 'Refresh Users' })] })] }), _jsxs(StatsGrid, { id: "tour-user-stats", children: [_jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: userStats.total }), _jsx("div", { className: "stat-label", children: "Total Users" })] }), _jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: userStats.active }), _jsx("div", { className: "stat-label", children: "Active Users" })] }), _jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: userStats.pending }), _jsx("div", { className: "stat-label", children: "Pending Approval" })] }), _jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: userStats.dealers }), _jsx("div", { className: "stat-label", children: "Active Dealers" })] })] }), _jsxs(MainContent, { children: [error && (_jsxs(ErrorBanner, { children: [_jsx("strong", { children: "Error:" }), " ", error, _jsxs("small", { children: ["Open browser console and run ", _jsx("code", { children: "window.debugUserManagement()" }), " for detailed diagnostics"] })] })), _jsxs(SearchFilterBar, { id: "tour-search-filter-bar", children: [_jsxs(SearchInputWrapper, { children: [_jsx(InputLabel, { htmlFor: "search", children: "Search Users" }), _jsx(SearchInput, { id: "search", type: "text", placeholder: "Search by name, email, or business...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) })] }), _jsxs(FilterSelectWrapper, { children: [_jsx(InputLabel, { htmlFor: "roleFilter", children: "Filter by Role" }), _jsxs(FilterSelect, { id: "roleFilter", value: roleFilter, onChange: (e) => setRoleFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All Roles" }), Object.entries(ROLE_LABELS).map(([level, label]) => (_jsx("option", { value: Object.keys(RoleLevel)[parseInt(level) - 1], children: label }, level)))] })] })] }), _jsxs(SectionTitle, { children: ["Users ", _jsxs("span", { children: ["(", filteredUsers.length, ")"] })] }), _jsx(UsersGrid, { children: filteredUsers.map(user => (_jsxs(UserCard, { children: [_jsxs(UserCardHeader, { children: [_jsxs(UserInfo, { children: [_jsxs(UserName, { children: [user.profile.firstName, " ", user.profile.lastName] }), _jsx(UserEmail, { children: user.email }), user.profile.businessName && (_jsx(UserBusiness, { children: user.profile.businessName }))] }), _jsxs(UserBadges, { children: [_jsx(StatusBadge, { "$status": user.status, children: user.status.toUpperCase() }), _jsx(RoleBadge, { "$color": getRoleColor(user.roleLevel), children: ROLE_LABELS[user.roleLevel] })] })] }), _jsx(UserCardBody, { children: _jsxs(UserDetails, { children: [_jsxs(DetailItem, { children: [_jsx("div", { className: "label", children: "Role Level" }), _jsxs("div", { className: "value", children: ["Level ", user.roleLevel] })] }), _jsxs(DetailItem, { children: [_jsx("div", { className: "label", children: "Email Verified" }), _jsx("div", { className: "value", children: user.emailVerified ? 'Yes' : 'No' })] }), _jsxs(DetailItem, { children: [_jsx("div", { className: "label", children: "Last Login" }), _jsx("div", { className: "value", children: user.lastLoginAt
                                                            ? new Date(user.lastLoginAt).toLocaleDateString()
                                                            : 'Never' })] }), _jsxs(DetailItem, { children: [_jsx("div", { className: "label", children: "Member Since" }), _jsx("div", { className: "value", children: new Date(user.createdAt).toLocaleDateString() })] }), user.role === 'DEALER' && (_jsxs(DetailItem, { children: [_jsx("div", { className: "label", children: "Selling Permission" }), _jsx("div", { className: "value", children: _jsx(StatusBadge, { "$status": user.profile.customerInfo?.sellingPermission === 'permitted' ? 'active' : 'suspended', children: user.profile.customerInfo?.sellingPermission === 'permitted' ? 'PERMITTED' : 'DENIED' }) })] }))] }) }), _jsxs(UserCardActions, { children: [canManageUser(user.id, user.roleLevel) && (_jsx(AdminOnly, { children: _jsx(RoleSelector, { value: user.role, onChange: (e) => handleRoleChange(user.id, e.target.value), children: getAssignableRoleOptions(maxAssignableRole).map(option => (_jsx("option", { value: option.value, children: option.label }, option.value))) }) })), _jsx(AdminOnly, { children: _jsxs(RoleSelector, { value: user.status, onChange: (e) => handleStatusChange(user.id, e.target.value), children: [_jsx("option", { value: "active", children: "Active" }), _jsx("option", { value: "inactive", children: "Inactive" }), _jsx("option", { value: "suspended", children: "Suspended" }), _jsx("option", { value: "pending", children: "Pending" })] }) }), _jsx(ActionButton, { "$variant": "secondary", onClick: () => handleViewProfile(user), children: "View Profile" }), _jsx(SuperAdminOnly, { children: _jsx(ActionButton, { "$variant": "primary", onClick: () => handleEditPermissions(user), children: "Edit Permissions" }) }), user.role === 'DEALER' && (_jsx(AdminOnly, { children: _jsx(ActionButton, { "$variant": user.profile.customerInfo?.sellingPermission === 'permitted' ? 'danger' : 'success', onClick: () => handleSellingPermissionToggle(user.id, user.profile.customerInfo?.sellingPermission), children: user.profile.customerInfo?.sellingPermission === 'permitted' ? 'Deny Selling' : 'Permit Selling' }) })), user.status === 'pending' && (_jsx(AdminOnly, { children: _jsx(ActionButton, { "$variant": "success", onClick: () => handleStatusChange(user.id, 'active'), children: "Approve" }) }))] })] }, user.id))) }), filteredUsers.length === 0 && (_jsx(EmptyState, { children: _jsx("p", { children: "No users found matching your criteria." }) })), _jsxs(AdminSection, { children: [_jsx(AdminSectionTitle, { children: "Administrative Actions" }), _jsxs(AdminActionsGrid, { children: [_jsx(SuperAdminOnly, { children: _jsx(SecondaryButton, { onClick: () => {
                                                try {
                                                    if (users.length === 0) {
                                                        notify.warning('No Data', 'No users to export');
                                                        return;
                                                    }
                                                    const headers = ['Email', 'First Name', 'Last Name', 'Business', 'Role', 'Status', 'Last Login', 'Created'];
                                                    const rows = users.map(u => [
                                                        u.email,
                                                        u.profile?.firstName || '',
                                                        u.profile?.lastName || '',
                                                        u.profile?.businessName || '',
                                                        ROLE_LABELS[u.roleLevel] || `Level ${u.roleLevel}`,
                                                        u.status,
                                                        u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never',
                                                        new Date(u.createdAt).toLocaleDateString()
                                                    ]);
                                                    const csvContent = [headers, ...rows]
                                                        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                                                        .join('\n');
                                                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                                                    const url = window.URL.createObjectURL(blob);
                                                    const link = document.createElement('a');
                                                    link.href = url;
                                                    link.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
                                                    link.click();
                                                    window.URL.revokeObjectURL(url);
                                                    notify.success('Export Complete', `Exported ${users.length} users to CSV`);
                                                }
                                                catch (error) {
                                                    notify.error('Export Failed', 'Failed to export users');
                                                }
                                            }, children: "Export User Data" }) }), _jsx(SuperAdminOnly, { children: _jsx(SecondaryButton, { onClick: () => notify.info('Coming Soon', 'Bulk role update will be available in a future update.'), children: "Bulk Role Update" }) }), _jsx(SuperAdminOnly, { children: _jsx(SecondaryButton, { onClick: () => notify.info('Coming Soon', 'Permission templates will be available in a future update.'), children: "Permission Templates" }) }), _jsx(AdminOnly, { children: _jsx(SecondaryButton, { onClick: () => navigate('/admin/analytics'), children: "System Analytics" }) })] })] })] }), selectedUser && (_jsxs(_Fragment, { children: [_jsx(UserProfileModal, { user: selectedUser, isOpen: isProfileModalOpen, onClose: () => {
                            setIsProfileModalOpen(false);
                            setSelectedUser(null);
                            setUserDetails(null);
                        }, userDetails: userDetails }), _jsx(EditPermissionsModal, { user: selectedUser, isOpen: isPermissionsModalOpen, onClose: () => {
                            setIsPermissionsModalOpen(false);
                            setSelectedUser(null);
                        }, onSave: handleSavePermissions })] })), _jsx(PreRegistrationModal, { isOpen: isPreRegistrationModalOpen, onClose: () => setIsPreRegistrationModalOpen(false), currentUserRole: currentUser?.roleLevel || RoleLevel.BASIC_DEALER })] }));
};
export default UserManagement;
